import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

export default function VisionSection() {
  const { problemStats } = useLandingData();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="producto" ref={ref} className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div
            className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
          >
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
              {t("vision_label")}
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("vision_title")}
            </h2>
            <p className="text-stone-500 text-lg leading-relaxed mb-6">
              {t("vision_p1")}
            </p>
            <p
              className="text-stone-500 text-lg leading-relaxed mb-8"
              dangerouslySetInnerHTML={{
                __html: t("vision_p2").replace(
                  "<strong>",
                  '<strong class="text-stone-800">'
                ),
              }}
            />
            <div className="flex items-center gap-4 p-5 bg-amber-50 border border-amber-100 rounded-2xl">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100">
                <i className="ri-lightbulb-flash-line text-amber-600 text-2xl" />
              </div>
              <div>
                <p className="font-semibold text-stone-800 text-sm">{t("vision_insight_title")}</p>
                <p className="text-stone-500 text-sm mt-0.5">{t("vision_insight_desc")}</p>
              </div>
            </div>
          </div>

          <div
            className={`grid grid-cols-2 gap-4 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            {problemStats.map((stat, i) => (
              <div
                key={stat.value}
                className={`p-6 rounded-2xl border transition-all duration-300 cursor-default hover:-translate-y-1 ${
                  i % 2 === 0
                    ? "bg-stone-900 border-stone-800"
                    : "bg-white border-stone-100"
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-xl mb-4 ${
                    i % 2 === 0 ? "bg-amber-400/20" : "bg-amber-50"
                  }`}
                >
                  <i
                    className={`${stat.icon} text-xl ${
                      i % 2 === 0 ? "text-amber-400" : "text-amber-600"
                    }`}
                  />
                </div>
                <p
                  className={`text-3xl font-bold mb-2 ${
                    i % 2 === 0 ? "text-amber-400" : "text-stone-900"
                  }`}
                >
                  {stat.value}
                </p>
                <p className={`text-sm leading-snug ${i % 2 === 0 ? "text-stone-400" : "text-stone-500"}`}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
